import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import StudentDetails from "../StudentDetails";
import StudentApplicationList from "../StudentAppliactionList";
import { getStudentDetails } from "../../../services/admin.services";
import { setSelectedStudent } from "../../../state/reducers/appSlice";
import { ArrowLeft, Loader } from "lucide-react";

const StudentApplication = () => {
  const isSideBarOpen = useSelector((state) => state.application.isSideBarOpen);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { studentId } = useParams();
  const dispatch = useDispatch();

  const fetchStudentDetails = async () => {
    try {
      setLoading(true);
      const response = await getStudentDetails(studentId);
      dispatch(setSelectedStudent(response.data));
    } catch (error) {
      console.error("Error fetching student details:", error);
      setError(
        error.response?.data?.message || "Failed to fetch student details."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (studentId) {
      fetchStudentDetails();
    }
  }, [studentId]);

  return (
    <div
      className={`flex-1 transition-all duration-300 ${
        isSideBarOpen ? "ml-80" : "ml-0 md:ml-20"
      } min-h-screen bg-gray-50`}
    >
      {/* Page Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-8xl mx-auto px-6 py-4 flex items-center">
          <button
            className="text-gray-600 hover:text-purple-600 rounded p-1 transition mr-3 flex items-center"
            onClick={() => window.history.back()}
          >
            <ArrowLeft size={16} />
            <span className="ml-1 text-sm">Back</span>
          </button>
          <h1 className="text-xl font-semibold text-gray-800">
            Student Application
          </h1>
        </div>
      </div>

      {/* Main Content Container */}
      <div
        className={`max-w-8xl  px-4 py-6 flex-1 transition-all duration-300 ${
          isSideBarOpen ? "ml-4" : "ml-0 md:ml-4"
        } min-h-screen bg-gray-50`}
      >
        {loading ? (
          /* Loading State */
          <div className="flex items-center justify-center h-64 bg-white rounded p-8 border border-gray-200">
            <div className="flex flex-col items-center">
              <Loader className="h-10 w-10 text-purple-500 animate-spin" />
              <p className="mt-4 text-gray-600">
                Loading student information...
              </p>
            </div>
          </div>
        ) : error ? (
          /* Error State */
          <div className="bg-red-50 rounded border border-red-200 p-8">
            <div className="flex flex-col items-center text-center mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-12 text-red-500 mb-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h2 className="text-lg font-medium text-red-800">
                Unable to load student data
              </h2>
              <p className="text-red-600 mt-2">{error}</p>
            </div>
            <div className="flex justify-center">
              <button
                onClick={fetchStudentDetails}
                className="px-5 py-2 bg-red-100 hover:bg-red-200 text-red-800 rounded text-sm font-medium transition-colors duration-200 flex items-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Try Again
              </button>
            </div>
          </div>
        ) : (
          /* Content When Data is Loaded */
          <div className="grid gap-6">
            {/* Student Details Card */}
            <div>
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                Student Information
              </h2>
              <div className="bg-pink-100 rounded overflow-hidden border border-gray-200">
                <StudentDetails />
              </div>
            </div>

            {/* Application List Card */}
            <div>
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                Application History
              </h2>
              <div className="bg-white rounded overflow-hidden border border-gray-200">
                <StudentApplicationList
                  fetchStudentDetails={fetchStudentDetails}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentApplication;
